import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { leaderboardStore } from '../stores/leaderboardStore'
import { gamificationStore } from '../stores/gamificationStore'
import { playSound, vibrate } from '../utils/sounds'
import GameLeaderboard from '../components/GameLeaderboard'

const BOARD_SIZE = 8

// Chess piece types with emoji equivalents
type PieceType = 'king' | 'queen' | 'rook' | 'bishop' | 'knight' | 'pawn'

interface Piece {
  type: PieceType
  isPlayer: boolean
  hasMoved?: boolean
  theme?: number
}

// Different piece themes for each level
const LEVEL_THEMES = [
  {
    name: 'Forest',
    emoji: '🌲',
    player: { king: '🦁', queen: '🦊', rook: '🐘', bishop: '🦅', knight: '🐴', pawn: '🐱' },
    enemy: { king: '🐲', queen: '👹', rook: '🗿', bishop: '👻', knight: '🦇', pawn: '💀' }
  },
  {
    name: 'Castle',
    emoji: '🏰',
    player: { king: '🤴', queen: '👸', rook: '🏰', bishop: '🧙', knight: '🦄', pawn: '⚔️' },
    enemy: { king: '👿', queen: '🧟', rook: '🏚️', bishop: '🦹', knight: '🐺', pawn: '💣' }
  },
  {
    name: 'Ocean',
    emoji: '🌊',
    player: { king: '🐳', queen: '🧜', rook: '🐢', bishop: '🐬', knight: '🦈', pawn: '🐠' },
    enemy: { king: '🦑', queen: '🐙', rook: '🦀', bishop: '🪼', knight: '🦞', pawn: '🐡' }
  },
  {
    name: 'Space',
    emoji: '🚀',
    player: { king: '👨‍🚀', queen: '🛸', rook: '🚀', bishop: '🌟', knight: '🤖', pawn: '🛰️' },
    enemy: { king: '👽', queen: '🛸', rook: '☄️', bishop: '🌑', knight: '👾', pawn: '🪨' }
  },
  {
    name: 'Food',
    emoji: '🍔',
    player: { king: '🍔', queen: '🍕', rook: '🌭', bishop: '🍟', knight: '🌮', pawn: '🍩' },
    enemy: { king: '🥗', queen: '🥒', rook: '🥕', bishop: '🥦', knight: '🍆', pawn: '🥬' }
  }
]

// Current theme index (will be set by component)
let currentTheme = 0

const getPieceEmoji = (piece: Piece): string => {
  const theme = LEVEL_THEMES[piece.theme ?? currentTheme] || LEVEL_THEMES[0]
  const pieces = piece.isPlayer ? theme.player : theme.enemy
  return pieces[piece.type]
}

// Get valid moves for a piece based on chess rules
const getValidMoves = (
  board: (Piece | null)[], 
  fromIndex: number,
  checkForCheck = true
): number[] => {
  const piece = board[fromIndex]
  if (!piece) return []
  
  const row = Math.floor(fromIndex / BOARD_SIZE)
  const col = fromIndex % BOARD_SIZE
  const moves: number[] = []
  
  const addMove = (r: number, c: number): boolean => {
    if (r < 0 || r >= BOARD_SIZE || c < 0 || c >= BOARD_SIZE) return false
    const idx = r * BOARD_SIZE + c
    const target = board[idx]
    if (target && target.isPlayer === piece.isPlayer) return false
    moves.push(idx)
    return !target // Continue if empty
  }
  
  const addLineMove = (dr: number, dc: number) => {
    for (let i = 1; i < BOARD_SIZE; i++) {
      if (!addMove(row + dr * i, col + dc * i)) break
    }
  }
  
  switch (piece.type) {
    case 'king':
      // One square in any direction
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr !== 0 || dc !== 0) addMove(row + dr, col + dc)
        }
      }
      break
      
    case 'queen':
      // Any direction, any distance
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr !== 0 || dc !== 0) addLineMove(dr, dc)
        }
      }
      break
      
    case 'rook':
      // Horizontal and vertical
      addLineMove(0, 1)
      addLineMove(0, -1)
      addLineMove(1, 0)
      addLineMove(-1, 0)
      break
      
    case 'bishop':
      // Diagonal
      addLineMove(1, 1)
      addLineMove(1, -1)
      addLineMove(-1, 1)
      addLineMove(-1, -1)
      break
      
    case 'knight':
      // L-shape moves
      const knightMoves = [
        [-2, -1], [-2, 1], [-1, -2], [-1, 2],
        [1, -2], [1, 2], [2, -1], [2, 1]
      ]
      knightMoves.forEach(([dr, dc]) => addMove(row + dr, col + dc))
      break
      
    case 'pawn':
      // Direction depends on side
      const direction = piece.isPlayer ? -1 : 1
      const startRow = piece.isPlayer ? 6 : 1
      
      // Forward move (can't capture)
      const forwardIdx = (row + direction) * BOARD_SIZE + col
      if (row + direction >= 0 && row + direction < BOARD_SIZE && !board[forwardIdx]) {
        moves.push(forwardIdx)
        
        // Double move from start
        if (row === startRow) {
          const doubleIdx = (row + direction * 2) * BOARD_SIZE + col
          if (!board[doubleIdx]) moves.push(doubleIdx)
        }
      }
      
      // Diagonal captures
      const captureLeft = (row + direction) * BOARD_SIZE + (col - 1)
      const captureRight = (row + direction) * BOARD_SIZE + (col + 1)
      if (col > 0 && board[captureLeft] && board[captureLeft]!.isPlayer !== piece.isPlayer) {
        moves.push(captureLeft)
      }
      if (col < 7 && board[captureRight] && board[captureRight]!.isPlayer !== piece.isPlayer) {
        moves.push(captureRight)
      }
      break
  }
  
  // Filter moves that would put own king in check (simplified)
  if (checkForCheck) {
    return moves.filter(toIdx => {
      const testBoard = [...board]
      testBoard[toIdx] = testBoard[fromIndex]
      testBoard[fromIndex] = null
      return !isKingInCheck(testBoard, piece.isPlayer)
    })
  }
  
  return moves
}

// Check if king is in check
const isKingInCheck = (board: (Piece | null)[], isPlayer: boolean): boolean => {
  // Find king position
  const kingIdx = board.findIndex(p => p?.type === 'king' && p.isPlayer === isPlayer)
  if (kingIdx === -1) return true // King captured = check
  
  // Check if any enemy piece can capture the king
  for (let i = 0; i < board.length; i++) {
    const piece = board[i]
    if (piece && piece.isPlayer !== isPlayer) {
      const moves = getValidMoves(board, i, false)
      if (moves.includes(kingIdx)) return true
    }
  }
  return false
}

// Información de cómo se mueve cada pieza
const PIECE_MOVES_INFO = {
  king: { name: 'Rey', emoji: '👑', moves: 'Una casilla en cualquier dirección' },
  queen: { name: 'Reina', emoji: '👸', moves: 'Cualquier número de casillas en cualquier dirección' },
  rook: { name: 'Torre', emoji: '🏰', moves: 'Cualquier número de casillas horizontal o vertical' },
  bishop: { name: 'Alfil', emoji: '⛪', moves: 'Cualquier número de casillas en diagonal' },
  knight: { name: 'Caballo', emoji: '🐴', moves: 'Movimiento en L: 2+1 casillas, puede saltar piezas' },
  pawn: { name: 'Peón', emoji: '♟️', moves: '1 casilla adelante (2 desde inicio), captura en diagonal' }
}

const ChessMoji = observer(() => {
  const [board, setBoard] = useState<(Piece | null)[]>([])
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [validMoves, setValidMoves] = useState<number[]>([])
  const [isPlayerTurn, setIsPlayerTurn] = useState(true)
  const [gameOver, setGameOver] = useState<'win' | 'lose' | null>(null)
  const [message, setMessage] = useState('')
  const [capturedByPlayer, setCapturedByPlayer] = useState<string[]>([])
  const [capturedByEnemy, setCapturedByEnemy] = useState<string[]>([])
  const [moveHistory, setMoveHistory] = useState<string[]>([])
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [level, setLevel] = useState(0)
  const [showHelp, setShowHelp] = useState(false)

  // Calcular score parcial basado en piezas capturadas
  // Valores: pawn=10, knight/bishop=30, rook=50, queen=90
  const liveScore = useMemo(() => {
    if (capturedByPlayer.length === 0) return 0
    
    // Los emojis varían por tema, así que contamos por cantidad aproximada
    // Cada pieza capturada vale puntos base + bonus por nivel
    const baseScorePerCapture = 50
    const levelBonus = level * 10
    return capturedByPlayer.length * (baseScorePerCapture + levelBonus)
  }, [capturedByPlayer.length, level])

  const sound = (type: Parameters<typeof playSound>[0]) => {
    if (soundEnabled) playSound(type)
  }

  // Initialize board with standard chess setup
  const initBoard = useCallback((themeIdx?: number) => {
    const theme = themeIdx ?? level
    currentTheme = theme // Update global theme for getPieceEmoji
    
    const newBoard: (Piece | null)[] = Array(64).fill(null)
    
    // Setup function
    const setupRow = (row: number, isPlayer: boolean, pieces: PieceType[]) => {
      pieces.forEach((type, col) => {
        newBoard[row * BOARD_SIZE + col] = { type, isPlayer, hasMoved: false, theme }
      })
    }
    
    const setupPawns = (row: number, isPlayer: boolean) => {
      for (let col = 0; col < 8; col++) {
        newBoard[row * BOARD_SIZE + col] = { type: 'pawn', isPlayer, hasMoved: false, theme }
      }
    }
    
    // Back row piece order (standard chess)
    const backRow: PieceType[] = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook']
    
    // Enemy pieces (top)
    setupRow(0, false, backRow)
    setupPawns(1, false)
    
    // Player pieces (bottom)
    setupPawns(6, true)
    setupRow(7, true, backRow)
    
    setBoard(newBoard)
    setSelectedIndex(null)
    setValidMoves([])
    setIsPlayerTurn(true)
    setGameOver(null)
    setCapturedByPlayer([])
    setCapturedByEnemy([])
    setMoveHistory([])
    setMessage('Your turn - select a piece')
    sound('go')
  }, [soundEnabled, level])

  useEffect(() => {
    initBoard()
  }, [initBoard])

  // Check for checkmate/stalemate
  useEffect(() => {
    if (board.length === 0 || gameOver) return
    
    const currentSide = isPlayerTurn
    let hasValidMove = false
    
    for (let i = 0; i < board.length; i++) {
      const piece = board[i]
      if (piece && piece.isPlayer === currentSide) {
        const moves = getValidMoves(board, i)
        if (moves.length > 0) {
          hasValidMove = true
          break
        }
      }
    }
    
    if (!hasValidMove) {
      const inCheck = isKingInCheck(board, currentSide)
      if (inCheck) {
        // Checkmate
        setGameOver(currentSide ? 'lose' : 'win')
        sound(currentSide ? 'lose' : 'win')
        vibrate(200)
        if (!currentSide) {
          const winScore = 500 + level * 100
          profileStore.addScore(winScore)
          leaderboardStore.submitGameScore('chessmoji', winScore, level)
          gamificationStore.recordGameResult('chessmoji', winScore, level, true)
        }
      } else {
        // Stalemate - draw (treat as loss for simplicity)
        setMessage('Stalemate!')
      }
    } else if (isKingInCheck(board, currentSide)) {
      setMessage(currentSide ? '⚠️ Check! Protect your King!' : '🎯 Enemy King in Check!')
      if (currentSide) sound('tick')
    }
  }, [board, isPlayerTurn, gameOver, level, soundEnabled])

  // AI move - receives current board state to avoid stale closure
  const executeAIMove = useCallback((currentBoard: (Piece | null)[]) => {
    setMessage('🤖 Enemy thinking...')
    
    setTimeout(() => {
      let bestMove: { from: number; to: number; score: number } | null = null
      
      // Simple AI: prioritize captures, check, then random
      for (let i = 0; i < currentBoard.length; i++) {
        const piece = currentBoard[i]
        if (piece && !piece.isPlayer) {
          const moves = getValidMoves(currentBoard, i)
          
          for (const to of moves) {
            let score = Math.random() * 10
            const target = currentBoard[to]
            
            // Prioritize captures by piece value
            if (target) {
              const values: Record<PieceType, number> = {
                pawn: 10, knight: 30, bishop: 30, rook: 50, queen: 90, king: 1000
              }
              score += values[target.type]
            }
            
            // Bonus for putting player in check
            const testBoard = [...currentBoard]
            testBoard[to] = testBoard[i]
            testBoard[i] = null
            if (isKingInCheck(testBoard, true)) {
              score += 50
            }
            
            // Bonus for advancing pawns
            if (piece.type === 'pawn') {
              score += (7 - Math.floor(to / BOARD_SIZE)) * 2
            }
            
            // Prefer center control
            const toCol = to % BOARD_SIZE
            const toRow = Math.floor(to / BOARD_SIZE)
            if (toCol >= 2 && toCol <= 5 && toRow >= 2 && toRow <= 5) {
              score += 5
            }
            
            if (!bestMove || score > bestMove.score) {
              bestMove = { from: i, to, score }
            }
          }
        }
      }
      
      if (bestMove) {
        const newBoard = [...currentBoard]
        const movingPiece = { ...newBoard[bestMove.from]!, hasMoved: true }
        const capturedPiece = newBoard[bestMove.to]
        
        // Pawn promotion
        if (movingPiece.type === 'pawn' && Math.floor(bestMove.to / BOARD_SIZE) === 7) {
          movingPiece.type = 'queen'
        }
        
        newBoard[bestMove.to] = movingPiece
        newBoard[bestMove.from] = null
        
        if (capturedPiece) {
          setCapturedByEnemy(prev => [...prev, getPieceEmoji(capturedPiece)])
          sound('match')
          vibrate(30)
          setMessage(`${getPieceEmoji(movingPiece)} captures ${getPieceEmoji(capturedPiece)}!`)
        } else {
          setMessage('Your turn')
        }
        
        setBoard(newBoard)
        setIsPlayerTurn(true)
      }
    }, 800)
  }, [soundEnabled])

  // Handle cell click
  const handleCellClick = (index: number) => {
    if (!isPlayerTurn || gameOver) return
    
    const piece = board[index]
    
    // If clicking on a valid move destination
    if (selectedIndex !== null && validMoves.includes(index)) {
      const newBoard = [...board]
      const movingPiece = { ...newBoard[selectedIndex]!, hasMoved: true }
      const capturedPiece = newBoard[index]
      
      // Pawn promotion
      if (movingPiece.type === 'pawn' && Math.floor(index / BOARD_SIZE) === 0) {
        movingPiece.type = 'queen'
        sound('levelUp')
        setMessage('🎉 Pawn promoted to Queen!')
      }
      
      newBoard[index] = movingPiece
      newBoard[selectedIndex] = null
      
      if (capturedPiece) {
        setCapturedByPlayer(prev => [...prev, getPieceEmoji(capturedPiece)])
        sound('match')
        vibrate(30)
        
        if (capturedPiece.type === 'king') {
          setGameOver('win')
          sound('win')
          const winScore = 500 + level * 100
          profileStore.addScore(winScore)
          leaderboardStore.submitGameScore('chessmoji', winScore, level)
          gamificationStore.recordGameResult('chessmoji', winScore, level, true)
          return
        }
      } else {
        sound('click')
      }
      
      const fromCol = String.fromCharCode(97 + (selectedIndex % 8))
      const fromRow = 8 - Math.floor(selectedIndex / 8)
      const toCol = String.fromCharCode(97 + (index % 8))
      const toRow = 8 - Math.floor(index / 8)
      setMoveHistory(prev => [...prev, `${getPieceEmoji(movingPiece)}${fromCol}${fromRow}-${toCol}${toRow}`])
      
      setBoard(newBoard)
      setSelectedIndex(null)
      setValidMoves([])
      setIsPlayerTurn(false)
      
      // Pass the updated board directly to avoid stale state
      setTimeout(() => executeAIMove(newBoard), 500)
      return
    }
    
    // Select a player piece
    if (piece && piece.isPlayer) {
      const moves = getValidMoves(board, index)
      setSelectedIndex(index)
      setValidMoves(moves)
      sound('click')
      setMessage(moves.length > 0 ? `${getPieceEmoji(piece)} - ${moves.length} moves available` : `${getPieceEmoji(piece)} - no valid moves`)
    } else {
      setSelectedIndex(null)
      setValidMoves([])
    }
  }

  return (
    <div className="min-h-screen chessmoji-bg flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 game-header">
        <div className="max-w-lg mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link to="/" className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70 text-gray-700">
                <span>←</span>
              </Link>
              <img src="/logo.png" alt="PlanetMoji" className="w-7 h-7 drop-shadow-lg" />
              <span className="font-title text-gray-800">ChessMoji</span>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="glass-pill text-xs py-1 px-3">
                <span>🏆</span>
                <span className="text-gray-800 ml-1">{profileStore.scoreForm}</span>
              </div>
              <button 
                onClick={() => setShowHelp(true)}
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70"
                title="Ayuda"
              >
                ❓
              </button>
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70"
              >
                {soundEnabled ? '🔊' : '🔇'}
              </button>
            </div>
          </div>
          
          {/* Turn indicator */}
          <div className="flex justify-center mt-2">
            <div className={`px-4 py-1.5 rounded-full text-sm font-medium ${isPlayerTurn ? 'bg-green-500/80 text-white' : 'bg-red-500/80 text-white'}`}>
              {isPlayerTurn ? '🎮 Your Turn' : '🤖 Enemy Thinking...'}
            </div>
          </div>
        </div>
      </header>
      
      {/* Captured pieces - Large display on sides */}
      <div className="fixed left-2 top-1/2 -translate-y-1/2 z-40 hidden sm:block">
        <div className="glass-card p-2 rounded-xl">
          <p className="text-[10px] text-gray-500 text-center mb-1">🏆 You</p>
          <div className="flex flex-col gap-0.5 text-2xl">
            {capturedByPlayer.length > 0 ? capturedByPlayer.map((p, i) => (
              <span key={i} className="animate-bounce-in">{p}</span>
            )) : <span className="text-gray-300">-</span>}
          </div>
        </div>
      </div>
      
      <div className="fixed right-2 top-1/2 -translate-y-1/2 z-40 hidden sm:block">
        <div className="glass-card p-2 rounded-xl">
          <p className="text-[10px] text-gray-500 text-center mb-1">💀 Lost</p>
          <div className="flex flex-col gap-0.5 text-2xl">
            {capturedByEnemy.length > 0 ? capturedByEnemy.map((p, i) => (
              <span key={i} className="animate-bounce-in">{p}</span>
            )) : <span className="text-gray-300">-</span>}
          </div>
        </div>
      </div>
      
      {/* Mobile captured display */}
      <div className="sm:hidden flex justify-between px-4 py-2 bg-white/30 backdrop-blur-sm border-b border-white/40">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">🏆</span>
          <span className="text-xl">{capturedByPlayer.join('') || '-'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xl">{capturedByEnemy.join('') || '-'}</span>
          <span className="text-xs text-gray-500">💀</span>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Message */}
        <div className="mb-3">
          <div className="glass-pill text-sm py-1.5 px-4">
            <span className="text-gray-800">{message}</span>
          </div>
        </div>
        
        {/* Chess Board */}
        <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-2 shadow-xl border border-white/50">
          <div className="grid grid-cols-8 gap-0.5" style={{ width: 'min(85vw, 400px)', height: 'min(85vw, 400px)' }}>
            {board.map((piece, index) => {
              const row = Math.floor(index / BOARD_SIZE)
              const col = index % BOARD_SIZE
              const isLight = (row + col) % 2 === 0
              const isSelected = selectedIndex === index
              const isValidMove = validMoves.includes(index)
              const isCapture = isValidMove && piece !== null
              
              return (
                <button
                  key={index}
                  onClick={() => handleCellClick(index)}
                  disabled={!!gameOver}
                  className={`
                    aspect-square flex items-center justify-center relative overflow-visible
                    transition-all duration-150
                    ${isLight ? 'bg-amber-100/30' : 'bg-amber-900/30'}
                    ${isSelected ? 'ring-2 ring-blue-400 bg-blue-400/40 scale-105 z-10' : ''}
                    ${isValidMove && !isCapture ? 'bg-green-500/40 ring-2 ring-green-400' : ''}
                    ${isCapture ? 'bg-red-400/40 ring-2 ring-red-400' : ''}
                    ${piece?.isPlayer && !gameOver ? 'cursor-pointer hover:brightness-110' : ''}
                  `}
                >
                  {/* Move indicator dot */}
                  {isValidMove && !piece && (
                    <div className="w-4 h-4 bg-green-400 rounded-full opacity-80" />
                  )}
                  
                  {/* Piece */}
                  {piece && (
                    <span className={`
                      text-3xl sm:text-4xl select-none drop-shadow-lg
                      ${isSelected ? 'scale-110' : ''}
                      ${isCapture ? 'opacity-70' : ''}
                    `}>
                      {getPieceEmoji(piece)}
                    </span>
                  )}
                  
                  {/* Attack indicator */}
                  {isCapture && (
                    <span className="absolute top-0 right-0 text-xs">⚔️</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex justify-center gap-6 mt-4 text-xs text-white/60">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-green-400/40 rounded-full" /> Move
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-red-400/40 rounded" /> Capture
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-blue-400/40 rounded" /> Selected
          </span>
        </div>

        {/* Level selector */}
        <div className="mt-4 glass-card p-4 w-full max-w-lg">
          <p className="text-center text-white/50 text-sm mb-3">Select Theme</p>
          <div className="flex justify-center gap-3 flex-wrap">
            {LEVEL_THEMES.map((theme, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setLevel(idx)
                  initBoard(idx)
                }}
                className={`
                  w-20 h-24 rounded-2xl flex flex-col items-center justify-center gap-1
                  transition-all duration-200
                  ${level === idx 
                    ? 'bg-white scale-105 shadow-xl ring-2 ring-amber-400' 
                    : 'bg-white/10 hover:bg-white/20 hover:scale-105'}
                `}
              >
                <span className="text-2xl">{theme.emoji}</span>
                <span className={`text-[10px] font-medium ${level === idx ? 'text-gray-600' : 'text-white/60'}`}>
                  {theme.name}
                </span>
                <div className="flex gap-0.5 text-sm">
                  <span>{theme.player.king}</span>
                  <span className="text-white/30">vs</span>
                  <span>{theme.enemy.king}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Mini Leaderboard - Live score based on captures */}
        <div className="mt-4 w-full max-w-lg">
          <GameLeaderboard 
            game="chessmoji"
            gameName="ChessMoji"
            emoji="♟️"
            currentScore={liveScore}
            gradient="from-purple-600 to-indigo-600"
            limit={5}
          />
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowHelp(false)}>
          <div className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 rounded-3xl p-6 max-w-md w-full shadow-2xl animate-bounce-in border border-white/20" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-title text-2xl text-white">♟️ Cómo Jugar</h2>
              <button 
                onClick={() => setShowHelp(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3">
              {Object.entries(PIECE_MOVES_INFO).map(([key, info]) => (
                <div key={key} className="flex items-start gap-3 bg-white/10 rounded-xl p-3">
                  <span className="text-3xl">{info.emoji}</span>
                  <div>
                    <p className="font-bold text-white">{info.name}</p>
                    <p className="text-sm text-white/70">{info.moves}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-amber-500/20 rounded-xl border border-amber-400/30">
              <p className="text-sm text-amber-200">
                💡 <strong>Tip:</strong> Selecciona una pieza para ver sus movimientos válidos (verde = mover, rojo = capturar)
              </p>
            </div>
            
            <button
              onClick={() => setShowHelp(false)}
              className="mt-4 w-full py-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-xl font-semibold hover:opacity-90"
            >
              ¡Entendido!
            </button>
          </div>
        </div>
      )}

      {/* Game Over Modal */}
      {gameOver && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl animate-bounce-in border border-gray-200">
            <span className="text-7xl block mb-4">
              {gameOver === 'win' ? '🏆' : '💀'}
            </span>
            <h2 className="font-title text-3xl mb-2">
              {gameOver === 'win' ? 'Victory!' : 'Defeated'}
            </h2>
            <p className="text-gray-500 mb-6">
              {gameOver === 'win' 
                ? `You earned ${500 + level * 100} points!` 
                : 'The enemy conquered your kingdom'}
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-gray-100 rounded-xl p-3">
                <p className="text-sm text-gray-500">Captured</p>
                <p className="text-xl">{capturedByPlayer.join('') || '-'}</p>
              </div>
              <div className="bg-gray-100 rounded-xl p-3">
                <p className="text-sm text-gray-500">Moves</p>
                <p className="text-2xl font-bold">{moveHistory.length}</p>
              </div>
            </div>
            
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => initBoard()}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
              >
                🔄 Rematch
              </button>
              <Link
                to="/"
                className="px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white rounded-xl font-semibold hover:opacity-90"
              >
                🏠 Home
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default ChessMoji
